import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";

import * as supportQueryServices from "../../services/supportQuery";
import { CustomNotification } from "../../util/CustomNotification";
import useValue from "../../hooks/useValue";
import useTablePagination from "../../hooks/useTablePagination";
import { DefaultPagination } from "../../constants/generalConstants";
import { updateOpenCount } from "../../appRedux/reducers/Common";

import type { SupportQueryRecord } from "../../shared/interface/SupportQuery.interface";

export const useSupportQuery = () => {
  const dispatch = useDispatch();
  /* STATE */
  const [searchedText, setSearchedText] = useState<string>("");
  const [supportQueryData, setSupportQueryData] = useState<SupportQueryRecord[]>([]);
  const [reFetch, setRefetch] = useState(true);
  const [selectedCustomerId, setSelectedCustomerId] = useState<number | null>(null);
  const [selectedLocationId, setSelectedLocationId] = useState<number | null>(null);

  /* MODAL STATE */
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<SupportQueryRecord | null>(null);

  /* CUSTOM HOOKS */
  const { value: loading, handleDisableValue: handleDisableLoading, handleEnableValue: handleEnableLoading } = useValue(false);
  const { pagination, handleChangePagination, handleChangeTotalSize, handleResetPagination } = useTablePagination(0);

  /* HANDLERS */
  const startIndex = ((pagination?.current ?? 1) - 1) * (pagination?.pageSize ?? 6);

  const handleChangeOnSearch = (value: string) => {
    handleResetPagination();
    const trimmedValue = value?.replace(/^\s+/, "");
    setSearchedText(trimmedValue);
    setRefetch(true);
  };

  const handleChangeSearch = (value: string) => {
    handleResetPagination();
    const trimmedValue = value?.replace(/^\s+/, "");
    setSearchedText(trimmedValue);
    if (!trimmedValue) {
      setRefetch(true);
    }
  };

  const updateStatus = async (id: number) => {
    try {
      const response = await supportQueryServices.closeQuery(id);
      CustomNotification.showSuccessMessage(response?.message);
    } catch (error) {
      CustomNotification.showErrorMessage(error);
    } finally {
      closeModal();
      setRefetch(true);
    }
  };

  const openModal = (record: SupportQueryRecord) => {
    setSelectedRecord(record);
    setIsModalVisible(true);
  };

  const closeModal = () => {
    setIsModalVisible(false);
    setSelectedRecord(null);
  };

  const handleConfirmClose = () => {
    if (selectedRecord) {
      updateStatus(selectedRecord.id);
      closeModal();
    }
  };

  const handleCancelClose = () => {
    closeModal();
  };

  const handleChangeCustomer = (customerId: number | null) => {
    setSelectedLocationId(null);
    setSelectedCustomerId(customerId);
  };
  const handleChangeLocation = (locationId: number | null) => {
    setSelectedLocationId(locationId);
  };

  /* EFFECTS */
  useEffect(() => {
    const fetchSupportQueryData = async () => {
      try {
        handleEnableLoading();

        const paramsObject = {
          searchQuery: searchedText,
          page: pagination?.current || DefaultPagination.PAGE,
          pageSize: pagination?.pageSize || DefaultPagination.PAGE_SIZE,
          customerId: selectedCustomerId,
          locationId: selectedLocationId
        };

        const response = await supportQueryServices.getAllSupportQueries(paramsObject);
        const openQueryCountResponse = await supportQueryServices.getAllOpenSupportQueryCount();

        const openCount = openQueryCountResponse?.data?.openCount || 0;
        dispatch(updateOpenCount(openCount));

        const formattedResponse = response?.data?.rows?.map((item, index) => {
          return {
            ...item,
            sr_no: startIndex + index + 1,
            id: item.id,
            status: item.is_closed ? "CLOSED" : "OPEN",
            user: `${item?.full_name ?? ""} <br/> ${item?.mobile_number ?? ""}`
          };
        });

        handleChangeTotalSize(response?.data?.count || 0);
        setSupportQueryData(formattedResponse);
      } catch (error) {
        CustomNotification.showErrorMessage(error);
      } finally {
        setRefetch(false);
        handleDisableLoading();
      }
    };

    if (reFetch) fetchSupportQueryData();
  }, [reFetch]);

  useEffect(() => {
    if (pagination?.current && pagination?.pageSize) {
      setRefetch(true);
    } else {
      setRefetch(false);
    }
  }, [pagination?.current, pagination?.pageSize, selectedCustomerId, selectedLocationId]);

  return {
    loading,
    searchedText,
    pagination,
    handleChangeOnSearch,
    handleChangeSearch,
    handleChangePagination,
    supportQueryData,
    updateStatus,
    isModalVisible,
    selectedRecord,
    openModal,
    closeModal,
    handleCancelClose,
    handleConfirmClose,
    selectedCustomerId,
    setSelectedCustomerId,
    selectedLocationId,
    setSelectedLocationId,
    handleChangeCustomer,
    handleChangeLocation
  };
};
